import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ArrowUpRight, LayoutDashboard, LogOut } from "lucide-react";

const NAV = [
  { label: "Products", to: "/products" },
  { label: "Forms", to: "/forms" },
  { label: "Resources", to: "/resources" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-black/5">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="nav-logo">
          <img src="/cdos-logo-mark.png" alt="CDOS — Centennial Dealership Operating System"
            className="h-11 w-auto transition-transform group-hover:scale-[1.03]" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                pathname === n.to ? "text-primary" : "text-foreground/80"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link
                to={user.role === "admin" ? "/admin" : "/portal"}
                data-testid="nav-portal-btn"
                className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 border border-border hover:border-primary hover:text-primary btn-lift"
              >
                <LayoutDashboard className="w-4 h-4" />
                {user.role === "admin" ? "Admin" : "My Portal"}
              </Link>
              <button
                onClick={() => { logout(); navigate("/"); }}
                data-testid="nav-logout-btn"
                className="text-muted-foreground hover:text-accent transition-colors"
                title="Log out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="text-sm font-medium hover:text-primary transition-colors">
                Sign in
              </Link>
              <Link
                to="/products"
                data-testid="nav-cta-btn"
                className="flex items-center gap-1 text-sm font-semibold bg-primary text-white px-4 py-2 btn-lift"
              >
                Get the System <ArrowUpRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </div>

        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/5 bg-white px-6 py-4 space-y-3">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block text-sm font-medium py-1">
              {n.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-border flex gap-3">
            {user ? (
              <Link to={user.role === "admin" ? "/admin" : "/portal"} onClick={() => setOpen(false)} className="text-sm font-semibold text-primary">
                Go to {user.role === "admin" ? "Admin" : "Portal"}
              </Link>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium">Sign in</Link>
                <Link to="/products" onClick={() => setOpen(false)} className="text-sm font-semibold text-primary">Get the System</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
